import { createContext, useContext, useMemo, type JSX, type ReactNode } from 'react';
import { useAdminTeams } from '@/hooks/useAdmin';
import { useMainScopeLabel, useTeamScope } from '@/hooks/useTeamScope';
import { useUrlState } from '@/hooks/useUrlState';
import {
  DEFAULT_INSIGHTS_PRESET,
  insightsWindows,
  parseInsightsPreset,
  windowParams,
  type InsightsPreset,
  type InsightsWindows,
} from '@/lib/insights';
import { teamLabel } from '@/lib/labels';

/**
 * The scope every Insights tab shares: the period (and the same-length one
 * before it, for comparisons) and the team. The period lives in the URL
 * (`?period=&from=&to=`) and the team comes from useTeamScope — the
 * header's switcher, or a team section's lock — so a scoped view can be
 * linked. With no team picked the main teams are shown and the separate
 * sections (Influencer, Vyral) are left out, like every main admin view.
 */
export interface InsightsScope {
  preset: InsightsPreset;
  custom: { from: string; to: string };
  windows: InsightsWindows;
  /** Current window + team params for the API. */
  params: Record<string, string>;
  /** The period before, same team params. */
  previousParams: Record<string, string>;
  /** Picked or pinned team code, '' for the main scope. */
  team: string;
  locked: boolean;
  /** "Social Media + Vyral", or the team's name. */
  scopeLabel: string;
  teamName: (code: string, displayName?: string | null) => string;
  setPreset: (p: InsightsPreset) => void;
  setCustom: (r: { from: string; to: string }) => void;
  setTeam: (code: string) => void;
}

const Ctx = createContext<InsightsScope | null>(null);

export function useInsightsScope(): InsightsScope {
  const scope = useContext(Ctx);
  if (!scope) throw new Error('useInsightsScope must be used inside <InsightsScopeProvider>');
  return scope;
}

export function InsightsScopeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [url, setUrl] = useUrlState({ period: DEFAULT_INSIGHTS_PRESET as string, from: '', to: '' });
  const { team, setTeam, locked, params: teamParams } = useTeamScope();
  const mainLabel = useMainScopeLabel();
  const teams = useAdminTeams();
  const preset = parseInsightsPreset(url.period);
  const { from, to } = url;
  // Whole UTC days: the windows (and so the query keys) hold for the day.
  const todayKey = new Date().toISOString().slice(0, 10);

  const value = useMemo<InsightsScope>(() => {
    const custom = { from, to };
    const windows = insightsWindows(preset, custom);
    const byCode = new Map((teams.data ?? []).map((t) => [t.code, t.displayName]));
    const teamName = (code: string, displayName?: string | null): string =>
      teamLabel(code, displayName ?? byCode.get(code));
    return {
      preset,
      custom,
      windows,
      params: { ...windowParams(windows.current), ...(teamParams as Record<string, string>) },
      previousParams: { ...windowParams(windows.previous), ...(teamParams as Record<string, string>) },
      team,
      locked,
      scopeLabel: team ? teamName(team) : mainLabel,
      teamName,
      setPreset: (p) => setUrl(p === 'custom' ? { period: p } : { period: p, from: '', to: '' }),
      setCustom: (r) => setUrl({ period: 'custom', from: r.from, to: r.to }),
      setTeam,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, from, to, team, locked, teamParams, mainLabel, teams.data, todayKey]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
